/**
 * StudioChatInput.js
 *
 * Bottom input bar:
 *   📎 file upload | textarea (auto-resize) | send button
 *   Action row: Summarise | Analyse Requirements
 */
import React, { useRef, useEffect } from 'react';

function StudioChatInput({
  value,
  onChange,
  onSend,
  onFileUpload,
  onSummarise,
  onAnalyse,
  onFinalizePhase,
  disabled,
  canAnalyse,
  canFinalize = false,
  isStreaming,
  phaseLabel = 'Requirements',
}) {
  const taRef = useRef(null);
  const fileRef = useRef(null);

  // Auto-resize textarea
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 160) + 'px';
  }, [value]);

  // Keep typing flow smooth: restore focus after send/stream cycle completes.
  useEffect(() => {
    if (disabled || isStreaming) return;
    const ta = taRef.current;
    if (!ta) return;

    const raf = window.requestAnimationFrame(() => {
      ta.focus({ preventScroll: true });
      const len = ta.value?.length || 0;
      ta.setSelectionRange(len, len);
    });

    return () => window.cancelAnimationFrame(raf);
  }, [disabled, isStreaming]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && value.trim()) onSend();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      onFileUpload({ name: file.name, type: file.type, content: ev.target.result });
    };
    // Read as text for text-like files, otherwise skip content
    const isText = file.type.startsWith('text/') || /\.(txt|md|csv|json|yaml|yml)$/i.test(file.name);
    if (isText) reader.readAsText(file);
    else        reader.readAsDataURL(file); // base64 for other types
    e.target.value = '';
  };

  return (
    <div className="studio-input-area">
      <div className="studio-input-wrapper">
        {/* File upload */}
        <button
          className="studio-icon-btn"
          title="Attach file (requirements doc, notes, etc.)"
          disabled={disabled}
          onClick={() => fileRef.current?.click()}
        >
          📎
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".txt,.md,.csv,.json,.yaml,.yml,.pdf,.doc,.docx"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        {/* Text area */}
        <textarea
          ref={taRef}
          className="studio-textarea"
          placeholder={isStreaming ? 'Agent is thinking…' : 'Message…  (Shift+Enter for new line)'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled || isStreaming}
          rows={1}
        />

        {/* Send */}
        <button
          className="studio-send-btn"
          onClick={onSend}
          disabled={disabled || isStreaming || !value.trim()}
          title="Send (Enter)"
        >
          ↑
        </button>
      </div>

      {/* Action row */}
      <div className="studio-input-actions">
        <button
          className="studio-action-btn"
          onClick={onSummarise}
          disabled={disabled || isStreaming}
          title="Ask the agent to summarise everything gathered so far"
        >
          📝 Summarise Understanding
        </button>
        <button
          className={`studio-action-btn${canAnalyse ? ' primary' : ''}`}
          onClick={onAnalyse}
          disabled={disabled || isStreaming || !canAnalyse}
          title={canAnalyse ? 'Run requirement validation' : 'Need at least: idea, system type, and target users'}
        >
          🔍 Analyse {phaseLabel}
        </button>
        <button
          className="studio-action-btn"
          onClick={onFinalizePhase}
          disabled={disabled || isStreaming || !canFinalize}
          title={`Finalize ${phaseLabel} baseline and move to next phase`}
        >
          ✅ Finalize {phaseLabel}
        </button>
      </div>
    </div>
  );
}

export default StudioChatInput;
